import React from "react";

import SearchBar from "@/shared/components/ui/SearchBar/SearchBar";
import PaginationInfo from "@/shared/components/ui/Pagination/PaginationInfo";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { ListFilter, Repeat2 } from "lucide-react";
import {
  CONTACTS_GROUP_BY_OPTIONS,
  CONTACTS_GROUP_BY_PLACEHOLDER_ICON,
  MOVE_TO_OPTIONS,
} from "@/shared/utils/contactConfig";
import CommonGroupBy from "@/shared/components/ui/DropDown/CommonGroupBy";
import CommonSelect from "@/shared/components/ui/Select/CommonSelect";
import { useMainStore } from "@/stores/index";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";
import { NO_CAMPAIGNS_SCORE_HINT } from "@/features/campaigns/hooks/useCampaign";

const ContactHeader = ({
  searchValue,
  onSearchChange,
  totalContacts,
  currentPage = 1,
  rowsPerPage = 15,
  loadedCount,
  appliedFilterCount,
  selectedCount,
  selectionSummary: _selectionSummary,
  groupBy,
  onSetGroupBy,
  onFilterClick,
  onImportClick,
  onCampaignClick,
  isUploading,
  // onClearSelection,
  // onDeleteClick,
  isBulkDeleting,
  isMoving,
  onGenerateMatchScore,
  isGeneratingMatchScore,
  matchScoreDisabled = false,
  itemName = "contact",
}) => {
  const sidebarExpanded = useMainStore((state) => state.sidebarExpanded);

  return (
    <div className="flex justify-between items-center w-full gap-2 flex-wrap">
      <div className="flex gap-2 sm:items-center items-start sm:flex-row flex-col w-full min-w-0 flex-1">
        <SearchBar
          value={searchValue}
          setFilterValue={onSearchChange}
          placeholder="Search contacts..."
          className={`w-full  ${sidebarExpanded ? " max-w-[220px]" : "max-w-[280px]"} duration-300 transition-all`}
        />

        <PaginationInfo
          totalCount={totalContacts}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          loadedCount={loadedCount}
          itemName={itemName}
          suffixText={itemName === "group" ? "" : "added by you"}
        />
      </div>

      <div className="flex gap-2 items-center flex-wrap sm:justify-end justify-start">
        {selectedCount > 0 && (
          <CustomCommonTooltip
            content={matchScoreDisabled ? NO_CAMPAIGNS_SCORE_HINT : null}
          >
            {/* The wrapper keeps the tooltip working while the button is disabled. */}
            <span className="inline-flex">
              <SecondaryButton
                title="Generate Match Score"
                onClick={onGenerateMatchScore}
                icon={<Repeat2 size={16} className="text-secondary" />}
                className="border-none text-secondary bg-secondary/10"
                isLoading={isGeneratingMatchScore}
                disabled={matchScoreDisabled}
              />
            </span>
          </CustomCommonTooltip>
        )}

        {typeof onSetGroupBy === "function" && (
          <CommonGroupBy
            groupBy={groupBy}
            onSetGroupBy={onSetGroupBy}
            options={CONTACTS_GROUP_BY_OPTIONS}
            placeholderIcon={CONTACTS_GROUP_BY_PLACEHOLDER_ICON}
          />
        )}

        {appliedFilterCount >= 0 && (
          <SecondaryButton
            title={
              <span>
                Filters
                {appliedFilterCount > 0 && (
                  <span className="text-white text-[0.65rem] px-1.5 py-0.5 bg-secondary rounded-full ml-1">
                    {appliedFilterCount}
                  </span>
                )}
              </span>
            }
            icon={
              <ListFilter
                size={16}
                className={`${appliedFilterCount > 0 ? "text-secondary" : "text-gray-800 dark:text-gray-300"}`}
              />
            }
            onClick={onFilterClick}
            className={`shadow-none ${appliedFilterCount > 0 ? "border-secondary text-secondary bg-secondary/5" : ""}`}
          />
        )}

        {selectedCount > 0 ? (
          <CommonSelect
            options={MOVE_TO_OPTIONS}
            placeholder="Move to"
            onChange={onCampaignClick}
            disabled={isBulkDeleting || isMoving}
            width="w-[185px]"
            classNames={{
              trigger:
                "min-w-0 shrink-0 !bg-secondary !text-white border-secondary hover:!bg-secondary data-[hover=true]:!bg-secondary data-[open=true]:!bg-secondary",
              value:
                "!text-[0.715rem] !text-white data-[placeholder=true]:!text-white/80",
              popoverContent: "!min-w-[185px] dark:!bg-gray-900 dark:!border-gray-800",
              selectorIcon: "!text-white",
            }}
          />
        ) : (
          <div className="flex gap-2 items-center">
            <PrimaryButton onClick={onImportClick} disabled={isUploading}>
              {isUploading ? "Importing..." : "Import Contacts"}
            </PrimaryButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactHeader;
